import { eObstacleType } from "./ObstacleTypes";
import { ObstacleVersion, ObstacleCollisionArea } from "./ObstacleVersion";
import { ObstacleMapData } from "./ObstacleMapData";
import { ViewCellMapRow, eRowState } from "../View/ViewCellMapRow";
import { TileBase } from "../Ground Tiles/TileBase";
import { ObstacleManager } from "./ObstacleManager";
import { View } from "../View/View";
import { Player } from "../Player/Player";
import { Gameplay, eBatchGroup } from "../../Gameplay";
import { Pool } from "../../../Utils/Collections/Pool";
import { Game, eAtlas_0 } from "../../../Game";
import { TextureAtlasUtils } from "../../../Utils/TextuAtlasUtils";
import { GameWorld } from "../GameWorld";



export class Obstacle {
    protected static _TMP_BBOX = new pc.BoundingBox();
    protected static _TMP_BSPHERE = new pc.BoundingSphere();

    /**
     * Budou se testovat kolize s prekazkou v kazdem volani update()
     */
    protected static readonly FLAG_CHECK_COLLISION = 0x0001;

    /**
     * Ceka se na spusteni animace
     */
    protected static readonly FLAG_ACT_ANIM = 0x0002;

    protected _flags: number;

    /**
     * Sidleny pool s entitami pro falesny stin
     */
    protected static _SHADOW_POOL: Pool<pc.Entity>;

    /**
     * Radka, do ktere prekazka patri
     */
    protected _row: ViewCellMapRow;

    /**
     * Entita zobrazujici prekazky
     */
    protected _entity: pc.Entity;

    /**
     * Entita zobrazujici stin
     */
    protected _shadow: pc.Entity;

    /**
     * (RO) Typ prekazky
     */
    get type(): eObstacleType { return this._type; }
    protected _type: eObstacleType;

    /**
     * (RO) Verze prekazky
     */
    get version(): ObstacleVersion { return this._version; }
    protected _version: ObstacleVersion;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(type: eObstacleType) {
        if (!Obstacle._SHADOW_POOL) {
            Obstacle._SHADOW_POOL = new Pool<pc.Entity>(undefined, 8, true, (): pc.Entity => {
                let entity = new pc.Entity();

                entity.addComponent(pc.eCompomentType.element, {
                    type: pc.ELEMENTTYPE_IMAGE,
                    texture: Game.getTextureAtlas(0).texture,
                    anchor: [0.5, 0.5, 0.5, 0.5],
                    pivot: [0.5, 0.5],
                    batchGroupId: Gameplay.BATCH_GROUP_ID[eBatchGroup.fakeShadows]
                });

                entity.setLocalEulerAngles(-90, 0, 0);
                entity.enabled = false;

                return entity;
            });
        }

        this._type = type;

        // Vytvorit entitu
        this._entity = new pc.Entity();
        this._entity.addComponent(pc.eCompomentType.model, {
            type: pc.eModelType.asset,
            batchGroupId: Gameplay.BATCH_GROUP_ID[eBatchGroup.solid_1],
            castShadows: false,
            castShadowsLightmap: false,
            receiveShadows: false,
            lightmapped: false,
        });
        this._entity.enabled = false;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    init(mapData: ObstacleMapData, row: ViewCellMapRow): void {
        this._row = row;
        this._flags = Obstacle.FLAG_CHECK_COLLISION;

        // Nastavit entitu
        let version = ObstacleManager.OBS_VERSIONS[mapData.settings.type][mapData.settings.version];

        if (this._version != version) {
            this._version = version;
            this._entity.model.asset = version.modelAsset;

            if (version.animation) {
                // Animace
                let entAnim = this._entity.animation;
                let verAnim = version.animation;

                if (!entAnim) {
                    entAnim = this._entity.addComponent(pc.eCompomentType.animation, {
                        activate: false
                    }) as pc.AnimationComponent;
                }

                entAnim.loop = verAnim.loop;
                entAnim.assets = [verAnim.asset];
            }
        }

        if (version.animation) {
            this._entity.animation.enabled = true;
            this._entity.animation.currentTime = 0;
            this._flags |= Obstacle.FLAG_ACT_ANIM;
        }
        else if (this._entity.animation) {
            this._entity.animation.enabled = false;
        }

        this._entity.setLocalPosition(mapData.cellId * TileBase.SIZE + TileBase.SIZE / 2, version.y, 0);
        this._entity.enabled = true;

        let rowGraphNode = row.graphNode;

        if (this._entity.parent != rowGraphNode)
            this._entity.reparent(rowGraphNode);

        // Stin
        if (version.shadow) {
            let settings = version.shadow;
            let atlas = Game.getTextureAtlas(0);

            this._shadow = Obstacle._SHADOW_POOL.getItem();
            this._shadow.setLocalScale(1, 1, 1);
            this._shadow.element.rect = TextureAtlasUtils.getTexFrameRegion(atlas.texture, (<pc.TextureAtlasFrame>atlas.frames[settings.frameKey]).rect);
            this._shadow.element.width = settings.width;
            this._shadow.element.height = settings.depth;

            if (settings.isLocal) {
                this._shadow.setLocalPosition(settings.offsetX, settings.offsetY, settings.offsetZ);

                if (this._shadow.parent != this._entity)
                    this._shadow.reparent(this._entity);
            }
            else {
                this._shadow.setLocalPosition(mapData.cellId * TileBase.SIZE + TileBase.SIZE / 2 + settings.offsetX, version.y + settings.offsetY, settings.offsetZ);

                if (this._shadow.parent != rowGraphNode)
                    this._shadow.reparent(rowGraphNode);
            }

            this._shadow.enabled = true;
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    release(): void {
        this._entity.enabled = false;

        if (this._shadow) {
            Obstacle._SHADOW_POOL.returnItem(this._shadow);
            this._shadow.enabled = false;
            this._shadow = undefined;
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): boolean {
        // Hlidat zajeti prekazky pod view
        if (this._row.state == eRowState.deactivation) {
            if (View.instance.botZ <= this._row.z - this._version.viewDeactOffset) {
                // Snizit pocet aktivnich prekazek na radce
                this.release();
                this._row.decActObstacles();

                return false;
            }
        }

        // Animace
        if ((this._flags & Obstacle.FLAG_ACT_ANIM) != 0) {
            if ((this._row.z + this._version.animation.actDistance) >= Player.instance.position.z) {
                // Spustit animace
                this._entity.animation.play(this._version.animation.asset.name);
                this._flags &= ~Obstacle.FLAG_ACT_ANIM;
            }
        }

        // Detekce kolize s hracem
        if ((this._flags & Obstacle.FLAG_CHECK_COLLISION) != 0) {
            let pl = Player.instance;

            if (pl.checkCollisions) {
                let pos = this._entity.getPosition();
                let colDis = this._version.colDistance;
                let plPos = pl.position;
                let plRadius = pl.colSphere.radius;

                if (Math.abs(plPos.z - pos.z) < colDis.z + plRadius && Math.abs(plPos.x - pos.x) < colDis.x + plRadius && Math.abs(plPos.y - pos.y) < colDis.y + plRadius) {
                    let i = this._version.colAreas.length;

                    while (i-- != 0) {
                        let colArea = this.getColArea(pos, i);

                        if (colArea.intersectsBoundingSphere(Player.instance.colSphere)) {
                            Player.instance.kill();
                            break;
                        }
                    }
                }
            }
        }

        return true;
    }

    //-------------------------------------------------------------------------
    /**
     * Vraci 3D colizni oblast prekazky (bud pc.BoundingBox nebo pc.BoundingSphere)
     * @param pos Aktualni pozice prekazky v hernim svete
     * @param id Index kolizni oblasti
     */
    //-------------------------------------------------------------------------
    protected getColArea(pos: pc.Vec3, id: number): pc.BoundingBox | pc.BoundingSphere {
        let colAreaS = this._version.colAreas[id];
        let colAreaSCenter = colAreaS.area.center;
        let isBox = colAreaS.isBox;
        let colAreaD = isBox ? Obstacle._TMP_BBOX : Obstacle._TMP_BSPHERE;

        // Nastavit pozici stredu
        colAreaD.center.set(pos.x + colAreaSCenter.x, pos.y + colAreaSCenter.y, pos.z + colAreaSCenter.z);

        if (isBox) {
            (<pc.BoundingBox>colAreaD).halfExtents.copy((<pc.BoundingBox>colAreaS.area).halfExtents);
        }
        else {
            (<pc.BoundingSphere>colAreaD).radius = (<pc.BoundingSphere>colAreaS.area).radius;
        }

        return colAreaD;
    }
}